import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import './DistrictsList.css';

const DistrictAdminsList = () => {
  const { districtId } = useParams();
  const [admins, setAdmins] = useState([]);
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editAdmin, setEditAdmin] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchDistrict();
    fetchAdmins();
  }, [districtId]);

  const fetchDistrict = async () => {
    try {
      const response = await axios.get(`/api/districts/${districtId}`);
      setDistrict(response.data);
    } catch (error) {
      console.error('Failed to fetch district:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`/api/users/district-users/${districtId}`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (adminId) => {
    if (!window.confirm('Send password reset email to this admin?')) {
      return;
    }

    try {
      await axios.post(`/api/users/${adminId}/reset-password`);
      alert('Password reset email sent');
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('Failed to send password reset email');
    }
  };

  const handleEdit = (admin) => {
    setEditAdmin(admin.id);
    setEditFormData({
      username: admin.username,
      email: admin.email,
      first_name: admin.first_name,
      last_name: admin.last_name,
      title: admin.title,
      role: admin.role
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/users/${editAdmin}`, editFormData);
      setEditAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Failed to update user:', error);
      alert('Failed to update user');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Change this user's permission level to ${newRole === 'district_admin' ? 'District Admin' : newRole === 'district_editor' ? 'District Editor' : 'District User'}?`)) {
      return;
    }

    try {
      const user = admins.find(a => a.id === userId);
      await axios.put(`/api/users/${userId}`, {
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        title: user.title,
        role: newRole
      });
      fetchAdmins();
    } catch (error) {
      console.error('Failed to update user role:', error);
      alert('Failed to update user role');
    }
  };

  const handleDelete = async (adminId) => {
    try {
      await axios.delete(`/api/users/${adminId}`);
      setAdmins(admins.filter(a => a.id !== adminId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete admin:', error);
      alert('Failed to delete admin');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>District Users - {district?.district_name}</h2>
        <Link to={`/superadmin/districts/${districtId}/admins/add`} className="btn-primary">Add District User</Link>
      </div>

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete District User</h3>
            <p>Are you sure you want to delete this district user? This action cannot be undone.</p>
            <div className="modal-actions">
              <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Email</th>
            <th>Name</th>
            <th>Title</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {admins.length === 0 ? (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center' }}>No district users found</td>
            </tr>
          ) : (
            admins.map(admin => (
              <tr key={admin.id}>
                {editAdmin === admin.id ? (
                  <>
                    <td>
                      <input
                        type="text"
                        value={editFormData.username}
                        onChange={(e) => setEditFormData({...editFormData, username: e.target.value})}
                        style={{ width: '100%', padding: '0.5rem' }}
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                        style={{ width: '100%', padding: '0.5rem' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={`${editFormData.first_name} ${editFormData.last_name}`}
                        onChange={(e) => {
                          const names = e.target.value.split(' ');
                          setEditFormData({
                            ...editFormData,
                            first_name: names[0] || '',
                            last_name: names.slice(1).join(' ') || ''
                          });
                        }}
                        style={{ width: '100%', padding: '0.5rem' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editFormData.title}
                        onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                        style={{ width: '100%', padding: '0.5rem' }}
                      />
                    </td>
                    <td>
                      <select
                        value={editFormData.role}
                        onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                        style={{ width: '100%', padding: '0.5rem' }}
                      >
                        <option value="district_admin">District Admin</option>
                        <option value="district_editor">District Editor</option>
                        <option value="district_user">District User</option>
                      </select>
                    </td>
                    <td>
                      <button onClick={handleUpdate} className="btn-edit">Save</button>
                      <button onClick={() => setEditAdmin(null)} className="btn-secondary">Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{admin.username}</td>
                    <td>{admin.email}</td>
                    <td>{admin.first_name} {admin.last_name}</td>
                    <td>{admin.title}</td>
                    <td>
                      <select
                        value={admin.role}
                        onChange={(e) => handleRoleChange(admin.id, e.target.value)}
                        style={{ padding: '0.25rem', fontSize: '0.9rem' }}
                      >
                        <option value="district_admin">District Admin</option>
                        <option value="district_editor">District Editor</option>
                        <option value="district_user">District User</option>
                      </select>
                    </td>
                    <td className="actions">
                      <button onClick={() => handleEdit(admin)} className="btn-edit">Edit</button>
                      <button onClick={() => handleResetPassword(admin.id)} className="btn-add-user">Reset Password</button>
                      <button onClick={() => setDeleteConfirm(admin.id)} className="btn-delete">Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DistrictAdminsList;

